import { CommonModule } from '@angular/common';
import {
  Component,
  effect,
  ElementRef,
  Input,
  OnInit,
  Renderer2,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { DialogModule } from 'primeng/dialog';
import { AppResource } from 'src/app/app.resource';
import { BaseComponent } from 'src/app/shared/components/base/base.component';
import { Step } from 'src/app/shared/models/model/concretes/components/step.model';
import {
  Stepper,
  StepShow,
  Subtitle,
  Title,
} from 'src/app/shared/models/model/concretes/components/stepper.model';
import { Subscription } from 'src/app/shared/models/model/concretes/shared/subcription.model';
import { SharedDataService } from 'src/app/shared/services/shared-data.service';
import { SubscriptionService } from 'src/app/shared/services/subscription.service';
import { ADDRESS_PATH, SUBSCRIPTION_PATH } from '../../constants/route.constants';
import { PathType } from '../../enum/path-type.enum';

@Component({
  selector: 'app-progress-circle',
  templateUrl: './progress-circle.component.html',
  imports: [CommonModule, DialogModule, RouterModule],
  standalone: true,
})
export class ProgressCircleComponent extends BaseComponent implements OnInit {
  @Input() progress: number = 0; // Pourcentage (de 0 à 100)
  @Input() size: number = 144.51;
  @Input() sizeWH: number = 52.26;
  @Input() coordinates: number = 26.13;
  @Input() radius: number = 23;
  @Input() step: number = 1;
  @Input() maxStep: number = 3;

  displayModal: boolean = false;
  steppers!: StepShow;
  stepper: Stepper = new Stepper([]);
  comparatorWarning: boolean = false;
  @ViewChild('comparatorWarn') comparatorWarn!: TemplateRef<void>;

  constructor(
    resources: AppResource,
    private el: ElementRef,
    private renderer: Renderer2,
    private sharedDataService: SharedDataService,
    private subscriptionService: SubscriptionService,
  ) {
    super(resources);

    effect(() => {
      const subSignal = this.sharedDataService.getSubscription();
      const index = this.sharedDataService.getStepIndex();

      if (subSignal != null && index() != -1) {
        this.updateStepper(subSignal());
      }
    });
  }

  override ngOnInit() {
    this.updateProgress();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['progress']) {
      this.updateProgress();
    }
  }

  //#region Public Method
  showDialog() {
    this.displayModal = true;
  }

  redirectStep(url: string) {
    if (this.isWorkflowComparator && !this.comparatorWarning) {
      const itemsToClick = [];

      this.popupReference = this.vyvDialogService.openCustomDialog({
        template: this.comparatorWarn,
        title: this.resource.error.attention,
        noDefaultAction: true,
      });
      return;
    }

    this.subscriptionService.nextStep(url);
    this.displayModal = false;
  }
  //#endregion

  //#region Private Method
  private updateProgress() {
    const circle = this.el.nativeElement.querySelector('svg circle:nth-child(2)');
    if (circle) {
      const dashoffset = this.size - (this.size * this.progress) / 100;
      this.renderer.setStyle(circle, 'stroke-dashoffset', dashoffset);
    }
  }

  private updateStepper(subscription: Subscription | null) {
    this.stepper.steps = [];

    subscription?.groupSteps.forEach((groupStep) => {
      // ✅ Filtrer les steps dès le départ
      const validSteps = groupStep.steps.filter(
        (step) => step.isDefault || step.isActive || step.isCompleted,
      );

      const stepMap = new Map<string, Step[]>();

      // Grouper les steps filtrés par title
      validSteps.forEach((step) => {
        const title = step.layoutInfo.title;

        if (!stepMap.has(title)) {
          stepMap.set(title, []);
        }

        stepMap.get(title)!.push(step);
      });

      const groupedSteps = Array.from(stepMap, ([libelle, steps]) => ({
        libelle,
        steps,
      }));

      this.stepper.steps.push(
        new StepShow(
          this.getGroupStepIsVisible(groupedSteps),
          groupedSteps.map(
            (group) =>
              new Title(
                group.libelle,
                group.steps.map(
                  (step) =>
                    new Subtitle(
                      step.layoutInfo.subtitle,
                      [SUBSCRIPTION_PATH, step.url].join('/'),
                      step.isDefault,
                      step.isActive,
                      step.isCompleted,
                      true,
                    ),
                ),
                this.getStepIsActive(group.steps),
              ),
          ),
        ),
      );
    });
  }

  cancel() {
    this.popupReference?.close();
  }

  validate() {
    this.cancel();
    this.displayModal = false;
    this.comparatorWarning = true;
    this.store.updateForceAddressNavigation(true);

    const indexes = this.sharedDataService.getSubscription()()?.findStepIndexesByUrl(ADDRESS_PATH);
    if (indexes) {
      this.sharedDataService.setGroupStep(indexes.groupIndex);
    }
    this.store.updatePath(PathType.CONFORT);
    this.subscriptionService.nextStep([SUBSCRIPTION_PATH, ADDRESS_PATH].join('/'));
  }

  private getGroupStepIsVisible(groupedSteps: { steps: Step[] }[]): boolean {
    return groupedSteps.some((group) => group.steps.some((step) => step.isActive));
  }

  private getStepIsActive(steps: Step[]): boolean {
    return steps.some((step) => step.isActive || step.isCompleted);
  }
  //#endregion
}
